// Angular imports
import { Component, OnInit, ViewChild, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';

// 3rd Party imports
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// CC-Common imports
import { CCLocalStorageService, IconConstants, DataGridFeaturesComponent, Utils } from 'cc-common';

// Component imports
import { MPBaseComponentWithGrid } from '../../../_base/mp-base-component-with-grid';

// Service imports
import { ScreenService } from '../../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { WorkflowSharedService } from '../../../shared-workflow-module/_services/workflow-shared.service';
import { WorkflowEditorService } from '../../_services/workflow-editor.service';

// Models imports
import { WorkFlowParametersDto } from '../../../shared-workflow-module/_models/workflow-parameters-dto';
import { WorkflowQueueResultDto } from '../../_models/workflow-queue/workflow-queue-result-dto';
import { WorkflowCommunityAttributeParameterDto } from '../../_models/workflow-search/workflow-community-attribute-parameter-dto';
import { ActionMenuEvent, ActionMenuInfo } from '../../../shared-module/_models/action-menu-info';

// constant imports
import { WorkflowConstants } from '../../../shared-workflow-module/_constants/worflow-constants';
import { StringFormatConstants } from '../../../_constants/string-format-constants';
import { MessageInterval } from '../../../_constants/messages-intervals';
import { WorkflowActionConstants } from '../../_constants/workflow-action-constants';

@Component({
    selector: 'app-workflow-queue-datagrid',
    templateUrl: './workflow-queue-datagrid.component.html',
    styleUrls: ['./workflow-queue-datagrid.component.scss']
})
export class WorkflowQueueDataGridComponent extends MPBaseComponentWithGrid implements OnInit, OnChanges {
    @ViewChild('workflowPopup', { static: false }) workflowPopup: DxPopupComponent;
    @ViewChild('queueGrid', { static: false }) queueGrid: DxDataGridComponent;
    @ViewChild('workflowQueuePopup', { static: false }) workflowQueuePopup: DxPopupComponent;
    @ViewChild('gridFeatures', { static: false }) gridFeatures: DataGridFeaturesComponent;

    @Input() storageKey: string;
    @Input() dataSource: any[];
    @Input() queueFilters: any[];
    @Input() isLiteMode: boolean;

    @Output() forceRefresh = new EventEmitter<boolean>();

    isSmallScreen: boolean;
    loadingVisible: boolean;

    selectedWorkflow: WorkflowQueueResultDto;
    selectedCommunityName: string;

    showWorkFlowNotesPopUp: boolean;
    showWorkFlowHistoryPopUp: boolean;
    showWorkFlowPopUp: boolean;
    showUsersListPopup: boolean;
    showWorkflowViewer = false;

    workflowEditorTitle: string;
    notesTitle: string;

    isActionAvailable = true;
    uniqueResponsibleUsers: Array<any> = [];

    showCommunityAttribution = false;
    actionMenuItems: Array<ActionMenuInfo> = [];

    dateFormat = StringFormatConstants.dateFormat;
    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconPDF: string = IconConstants.PDF_FILE_GRID;
    iconHistory: string = IconConstants.HISTORY;
    iconCancel: string = IconConstants.CANCEL;
    iconCheck: string = IconConstants.CHECK;
    iconRefresh: string = IconConstants.REFRESH;
    iconLock: string = IconConstants.LOCK;

    constructor(
        protected readonly screenService: ScreenService,
        protected readonly localStorageService: CCLocalStorageService,
        protected readonly helperService: HelperService,
        private readonly errorService: ServerErrorService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly workflowEditorService: WorkflowEditorService
    ) {
        super(screenService, localStorageService, helperService);
    }

    ngOnInit(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.screenService.changed.subscribe(() => {
            this.isSmallScreen = this.screenService.isSmallScreen();
        });

        this.prepareUniqueResponsibleUsers();
        this.getActionItems();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (Utils.notNullAndDefined(changes.queueFilters) && Utils.notNullOrEmpty(changes.queueFilters.currentValue)) {
            if (this.queueGrid && this.queueGrid.instance) {
                this.queueGrid.instance.filter(this.queueFilters);
            }
        }

        if (
            changes.isLiteMode &&
            (changes.isLiteMode.currentValue === true || changes.isLiteMode.currentValue === false)
        ) {
            this.isLiteMode = changes.isLiteMode.currentValue;
        }

        if (changes.dataSource && changes.dataSource.currentValue) {
            this.dataSource = changes.dataSource.currentValue;
            this.checkIfActionAvailable();
            this.prepareUniqueResponsibleUsers();
        }

        this.repaintQueuegrid();
    }

    onWorkflowViewerClose(): void {
        this.showWorkflowViewer = false;
    }

    onWorkflowClose(): void {
        this.showWorkFlowPopUp = false;
        this.workflowPopup.visible = false;
    }

    onLogClick(record: WorkflowQueueResultDto): void {
        this.selectedWorkflow = record;
        this.showWorkFlowHistoryPopUp = true;
    }

    onLogClose(): void {
        this.showWorkFlowHistoryPopUp = false;
    }

    onWorkflowNotesClick(record): void {
        this.selectedWorkflow = record;
        this.showWorkFlowNotesPopUp = true;
    }

    onWorkflowNotesPopClose(forceRefresh: boolean): void {
        this.showWorkFlowNotesPopUp = false;
        if (Utils.isTrue(forceRefresh)) {
            this.forceRefresh.emit(forceRefresh);
        }
    }

    showUsersList(record: WorkflowQueueResultDto): void {
        this.selectedWorkflow = record;
        this.showUsersListPopup = true;
    }

    onDownloadFile(row): void {
        const baseParams = new WorkFlowParametersDto();
        baseParams.workflowId = row.workflowId;
        baseParams.communityId = row.communityId;

        this.downloadInvoice(baseParams);
    }

    onGridContentReady(): void {
        if (this.queueGrid && this.queueGrid.instance) {
            let isGrouped = false;
            for (let i = 0; i < this.queueGrid.instance.columnCount(); i++) {
                if (this.queueGrid.instance.columnOption(i, 'groupIndex') > -1) {
                    isGrouped = true;
                    break;
                }
            }

            if (Utils.isFalse(isGrouped) && Utils.notNullOrEmpty(this.dataSource)) {
                let isMoreThanOneStatus = false;
                const status: string[] = [];
                for (const obj of this.dataSource) {
                    if (Utils.notInList(obj.status, status)) {
                        status.push(obj.status);
                    }
                    if (status.length > 1) {
                        isMoreThanOneStatus = true;
                        break;
                    }
                }

                if (Utils.isTrue(isMoreThanOneStatus)) {
                    this.queueGrid.instance.columnOption('status', 'groupIndex', 0);
                }
            }
        }
    }

    onCommunityAttributionClose(forceRefresh: boolean): void {
        this.showCommunityAttribution = false;
        if (Utils.isTrue(forceRefresh)) {
            this.forceRefresh.emit(forceRefresh);
        }
    }

    onCommunityAttributionSave(communityId: number): void {
        if (Utils.notNullAndDefined(communityId)) {
            const baseParam: WorkflowCommunityAttributeParameterDto = new WorkflowCommunityAttributeParameterDto();
            baseParam.workflowId = this.selectedWorkflow.workflowId;
            baseParam.communityId = communityId;

            this.workflowEditorService.attributeCommunity(baseParam).subscribe(
                () => {
                    notify('Community attribution successful', 'success', MessageInterval.successMsgInterval);

                    /* As discussed with Sandeep, on DEV-3952 */
                    // setTimeout(() => {
                    //     this.showWorkFlowPopUp = true;
                    //     this.selectedWorkflow.communityId = communityId;
                    //     this.workflowEditorTitle = `AAP Workflow #${this.selectedWorkflow.workflowId}`;
                    //     if (Utils.notNullOrEmpty(this.selectedWorkflow.community)) {
                    //         this.workflowEditorTitle += `, Community: ${this.selectedWorkflow.community}`;
                    //     }
                    // }, 500);
                },
                (error) => {
                    this.errorService.displayError(error);
                }
            );
        } else {
            this.selectedWorkflow = null;
        }
    }

    hasAction(record: WorkflowQueueResultDto): boolean {
        return Utils.isTrue(record.allowProcessing) || Utils.isTrue(record.allowAttributeCommunity);
    }

    isActionItemVisible(item: ActionMenuInfo, record: WorkflowQueueResultDto): boolean {
        return (
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.PROCESSING) &&
                Utils.isTrue(record.allowProcessing)) ||
            (Utils.equalsIgnoreCase(item.action, WorkflowActionConstants.ATTRIBUTE_COMMUNITY) &&
                Utils.isTrue(record.allowAttributeCommunity))
        );
    }

    onActionItemClick(event: ActionMenuEvent<WorkflowQueueResultDto>): void {
        this.selectedWorkflow = event.record;
        const actionItem = event.item.action;
        if (Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.PROCESSING)) {
            if (this.canProcessWorkflow(this.selectedWorkflow)) {
                this.showWorkFlowPopUp = true;
                this.workflowEditorTitle = `AAP Workflow #${this.selectedWorkflow.workflowId}`;
                // [ANG-2510] - Data Entry Steps - Do not prepopulate community
                if (
                    Utils.notNullOrEmpty(this.selectedWorkflow.community) &&
                    Utils.notEqualsIgnoreCase(this.selectedWorkflow.status, 'Scanned') &&
                    Utils.notEqualsIgnoreCase(this.selectedWorkflow.status, 'Aapverify')
                ) {
                    this.workflowEditorTitle += `, Community: ${this.selectedWorkflow.community}`;
                }
            } else {
                this.showWorkflowViewer = true;
            }
        } else if (Utils.equalsIgnoreCase(actionItem, WorkflowActionConstants.ATTRIBUTE_COMMUNITY)) {
            this.showCommunityAttribution = true;
        }
    }

    private canProcessWorkflow(record: WorkflowQueueResultDto): boolean {
        return (
            Utils.isTrue(record.allowProcessing) &&
            (Utils.equalsIgnoreCase(record.status, WorkflowConstants.SCANNED) ||
                Utils.equalsIgnoreCase(record.status, WorkflowConstants.AAPVERIFY) ||
                Utils.equalsIgnoreCase(record.status, WorkflowConstants.AAPSUPERVISOR) ||
                Utils.equalsIgnoreCase(record.status, WorkflowConstants.CAMINBOX) ||
                Utils.equalsIgnoreCase(record.status, WorkflowConstants.CAMINREVIEW) ||
                Utils.equalsIgnoreCase(record.status, WorkflowConstants.ACCTINBOX) ||
                Utils.equalsIgnoreCase(record.status, WorkflowConstants.ACCTINREVIEW) ||
                Utils.equalsIgnoreCase(record.status, WorkflowConstants.HOLD) ||
                Utils.equalsIgnoreCase(record.status, WorkflowConstants.DENIED) ||
                Utils.equalsIgnoreCase(record.status, WorkflowConstants.APPROVED))
        );
    }

    private downloadInvoice(baseParams: WorkFlowParametersDto): void {
        this.loadingVisible = true;
        this.workflowSharedService.downloadInvoice(baseParams).subscribe(
            (result: Blob) => {
                const fileURL: string = this.helperService.getFileUrlFromBlob(result);
                window.open(
                    fileURL,
                    '_blank',
                    'noopener,noreferrer,scrollbars=yes,status=yes,resizable=yes,menubar=yes,toolbar=yes,titlebar=yes'
                );
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private checkIfActionAvailable(): void {
        const actionsRows = this.dataSource.filter((d) => Utils.isTrue(d.allowProcessing));
        if (Utils.notNullOrEmpty(actionsRows)) {
            this.isActionAvailable = true;
        } else {
            this.isActionAvailable = false;
        }
    }

    private prepareUniqueResponsibleUsers(): void {
        this.uniqueResponsibleUsers = this.helperService.prepareUniqueResponsibleUsers(
            this.dataSource,
            'dashboardResponsibleUsers'
        );
    }

    private getActionItems(): void {
        this.actionMenuItems = [
            {
                action: WorkflowActionConstants.PROCESSING,
                name: 'Process Workflow',
                separator: true,
                disabled: false,
                icon: null,
                items: []
            },
            {
                action: WorkflowActionConstants.ATTRIBUTE_COMMUNITY,
                name: 'Attribute Community',
                disabled: false,
                separator: true,
                icon: null,
                items: []
            }
        ];
    }

    private repaintQueuegrid(): void {
        if (this.queueGrid && this.queueGrid.instance) {
            this.queueGrid.instance.repaint();
        }
    }
}
